import { useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CustomerPicker } from './customerPicker';
import { useApi } from '../../hooks';
import { Spinner } from '@/components/ui/spinner';
import { type CreatedPortalUser, type CustomerChoice, type PortalRole, type PortalUser, ROLE_LABELS, errorCode, errorText } from './portalUser';

export type PortalUserFormMode = { kind: 'create' } | { kind: 'edit'; userId: number };

type Props = {
  mode: PortalUserFormMode;
  onOpenChange: (open: boolean) => void;
  // Called after every create, save or deactivate attempt, whatever the answer, so the page re-reads its list.
  onChanged: () => void;
};

const sameCustomers = (a: CustomerChoice[], b: CustomerChoice[]) =>
  a.length === b.length && a.every((choice) => b.some((other) => other.customerId === choice.customerId));

const NO_SUCH_USER = 'No such portal user.';

export const PortalUserForm = ({ mode, onOpenChange, onChanged }: Props) => {
  const { createPortalUser, getPortalUser, setPortalUserCustomers, deactivatePortalUser } = useApi();
  const editing = mode.kind === 'edit';
  const userId = mode.kind === 'edit' ? mode.userId : null;
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole | null>('ndi:customer');
  const [customers, setCustomers] = useState<CustomerChoice[]>([]);
  // Edit mode: the customers as last read from the server, which Save is compared against.
  const [loadedCustomers, setLoadedCustomers] = useState<CustomerChoice[]>([]);
  const [loadedUser, setLoadedUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(editing);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  // Changing the key empties the picker's text after a selection has been moved into the chip list.
  const [pickerKey, setPickerKey] = useState(0);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  // Held only while the dialog is open; the server keeps no copy, so closing the dialog loses it.
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const submitting = useRef(false);
  const callbacks = useRef({ getPortalUser, onChanged });
  callbacks.current = { getPortalUser, onChanged };

  const applyUser = (user: PortalUser) => {
    setLoadedUser(user);
    setRole(user.role);
    setCustomers(user.customers);
    setLoadedCustomers(user.customers);
  };

  useEffect(() => {
    if (userId === null) {
      return undefined;
    }
    // A user id read from a hand-typed URL may not be a number; no user has such an id, so nothing is requested.
    if (!Number.isSafeInteger(userId) || userId <= 0) {
      setError(NO_SUCH_USER);
      setLoading(false);
      return undefined;
    }
    let current = true;
    setLoading(true);
    callbacks.current
      .getPortalUser(userId)
      .then((user: PortalUser) => {
        if (current) {
          applyUser(user);
        }
      })
      .catch((err: unknown) => {
        if (current) {
          setError(errorText(err, 'Failed to load portal user.'));
          // The user may be gone or changed, so the page re-reads its list.
          callbacks.current.onChanged();
        }
      })
      .finally(() => {
        if (current) {
          setLoading(false);
        }
      });
    return () => {
      current = false;
    };
  }, [userId]);

  const addCustomer = (choice: CustomerChoice | null) => {
    if (choice && !customers.some((c) => c.customerId === choice.customerId)) {
      // A customer user holds one customer, so picking another replaces it.
      setCustomers(role === 'ndi:customer' ? [choice] : [...customers, choice]);
    }
    setPickerKey(pickerKey + 1);
  };

  /* Runs one attempt at a time; the page re-reads its list after every attempt because the change may have happened whatever the answer. */
  const attempt = async (work: () => Promise<void>) => {
    // A second click before the first render settles would otherwise send the request twice.
    if (submitting.current) {
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await work();
    } finally {
      onChanged();
      submitting.current = false;
      setBusy(false);
    }
  };

  const save = () =>
    userId === null
      ? undefined
      : attempt(async () => {
          try {
            applyUser(
              await setPortalUserCustomers(
                userId,
                customers.map((c) => c.customerId)
              )
            );
            onOpenChange(false);
          } catch (err) {
            setError(errorText(err, 'Failed to save portal user.'));
            // The refusal may follow a change made elsewhere, so the form shows the user as the server now has them.
            try {
              applyUser(await getPortalUser(userId));
            } catch {
              // The save's message stays; the form keeps what it last read.
            }
          }
        });

  const deactivate = () => {
    return userId === null
      ? undefined
      : attempt(async () => {
          try {
            await deactivatePortalUser(userId);
            onOpenChange(false);
          } catch (err) {
            setError(errorText(err, 'Failed to deactivate portal user.'));
          }
        });
  };

  const create = () =>
    role === null
      ? undefined
      : attempt(async () => {
          try {
            const answer: CreatedPortalUser = await createPortalUser({
              email: email.trim(),
              fullName: fullName.trim(),
              role,
              customerIds: customers.map((c) => c.customerId),
              confirmPublicAddress: confirmed,
            });
            setTemporaryPassword(answer.temporaryPassword);
            // identity-bridge's user sync never creates a sign-in for an address at a personal mail provider
            // (the same list the server confirms against), so a person has to finish that one by hand.
            const signIn = confirmed
              ? `${email.trim()} is a personal e-mail address, so portal sign-in is not set up automatically. Ask engineering to finish it.`
              : `${email.trim()} will be able to sign in in about 15 minutes.`;
            setCreated(
              answer.temporaryPassword
                ? `Created. ${signIn}`
                : `${email.trim()} already had an account, so their password is unchanged and they now have access. ${signIn}`
            );
          } catch (err) {
            if (errorCode(err) === 'public_address_unconfirmed') {
              setNeedsConfirmation(true);
            }
            setError(errorText(err, 'Failed to create portal user.'));
          }
        });

  const customerRule = role !== 'ndi:customer' || customers.length === 1;
  const hasCustomers = customers.length > 0 && customerRule;
  const ready = fullName.trim() !== '' && email.trim() !== '' && hasCustomers && (!needsConfirmation || confirmed);
  const canSave = hasCustomers && !sameCustomers(customers, loadedCustomers);
  const showForm = !editing || loadedUser !== null;

  const readOnlyField = (label: string, value: string | null) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <p className="text-sm">{value ?? '—'}</p>
    </div>
  );

  return (
    <Dialog open onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{confirmingDeactivate ? 'Deactivate user' : editing ? 'Portal user' : 'Add portal user'}</DialogTitle>
          <DialogDescription>
            {confirmingDeactivate
              ? `${loadedUser?.fullName ?? 'This user'} loses access to every customer and their portal role is removed.`
              : editing
              ? "Name, e-mail and role can't be changed here yet."
              : 'Creates the user and grants access to the customers chosen.'}
          </DialogDescription>
        </DialogHeader>
        {confirmingDeactivate ? (
          <>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => setConfirmingDeactivate(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={busy} onClick={deactivate}>
                Deactivate
              </Button>
            </DialogFooter>
          </>
        ) : created ? (
          <>
            <Alert>
              <AlertDescription>{created}</AlertDescription>
            </Alert>
            {temporaryPassword ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="portal-user-password" className="text-sm font-medium">
                  Temporary password
                </label>
                <div className="flex gap-2">
                  <Input id="portal-user-password" readOnly value={temporaryPassword} className="font-mono" />
                  <Button
                    variant="outline"
                    onClick={async () => {
                      await navigator.clipboard.writeText(temporaryPassword);
                      setCopied(true);
                    }}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Send it to {email.trim()} with the sign-in instructions. They must change it at their first sign-in. It is not shown again.
                </p>
              </div>
            ) : null}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {loading ? <Spinner aria-label="Loading" className="text-muted-foreground" /> : null}
              {showForm ? (
                <>
                  {editing ? (
                    readOnlyField('Full name', loadedUser?.fullName ?? null)
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="portal-user-name" className="text-sm font-medium">
                        Full name
                      </label>
                      <Input id="portal-user-name" value={fullName} onChange={(event) => setFullName(event.target.value)} />
                    </div>
                  )}
                  {editing ? (
                    readOnlyField('E-mail', loadedUser?.email ?? null)
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="portal-user-email" className="text-sm font-medium">
                        E-mail
                      </label>
                      <Input
                        id="portal-user-email"
                        type="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          // A confirmation covers the address it was given for, not the next one typed.
                          setNeedsConfirmation(false);
                          setConfirmed(false);
                        }}
                      />
                    </div>
                  )}
                  {editing ? (
                    readOnlyField('Role', role ? ROLE_LABELS[role] : null)
                  ) : (
                    <RadioGroup
                      value={role}
                      onValueChange={(next) => {
                        setRole(next as PortalRole);
                        if (next === 'ndi:customer') {
                          setCustomers(customers.slice(0, 1));
                        }
                      }}
                      aria-label="Role"
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="ndi:customer" />
                        Customer (one customer)
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="ndi:broker" />
                        Broker (one or more customers)
                      </label>
                    </RadioGroup>
                  )}
                  <div className="flex flex-col gap-2">
                    <CustomerPicker key={pickerKey} value={null} onChangeValue={addCustomer} label="Customers" />
                    {customers.length ? (
                      <ul className="flex flex-wrap gap-1.5">
                        {customers.map((chosen) => (
                          <li key={chosen.customerId} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs">
                            {chosen.name}
                            <button
                              type="button"
                              aria-label={`Remove ${chosen.name}`}
                              onClick={() => setCustomers(customers.filter((c) => c.customerId !== chosen.customerId))}
                            >
                              <XIcon className="size-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {editing && role === 'ndi:customer' && customers.length > 1 ? (
                      <p className="text-sm text-muted-foreground">A customer user holds one customer. Remove the others to save.</p>
                    ) : null}
                  </div>
                </>
              ) : null}
              {needsConfirmation ? (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={confirmed} onCheckedChange={(next) => setConfirmed(next === true)} />
                  This is a personal e-mail address. An engineer has to finish its portal sign-in — confirm
                </label>
              ) : null}
              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              {editing && loadedUser ? (
                <Button variant="destructive" className="sm:mr-auto" disabled={busy} onClick={() => setConfirmingDeactivate(true)}>
                  Deactivate
                </Button>
              ) : null}
              <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {editing ? (
                <Button disabled={busy || !loadedUser || !canSave} onClick={save}>
                  Save
                </Button>
              ) : (
                <Button disabled={busy || !ready} onClick={create}>
                  Create
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
